import { Link } from "react-router-dom";
import {
    ArrowRight,
    Phone,
    ShieldCheck,
    Hammer,
    Award,
    Clock,
    MapPin,
    Star,
    BadgeCheck,
} from "lucide-react";
import { Section } from "@/components/ui/section";
import { Container } from "@/components/ui/container";
import { BrandButton } from "@/components/ui/brand-button";
import FAQAccordion from "@/components/FAQAccordion";
import Seo from "@/components/Seo";
import QuickQuoteForm from "@/components/QuickQuoteForm";
import { usePhoneNumber } from "@/hooks/usePhoneNumber";
import { cn } from "@/lib/utils";
import { TRACKS } from "@/lib/tracks";
import { PAGE_META, REVIEW_SOURCES } from "@/lib/seo-constants";
import {
    localBusinessSchema,
    serviceSchema,
    faqPageSchema,
    breadcrumbListSchema,
} from "@/lib/jsonld";
import { TESTIMONIALS } from "@/data/testimonials";
import { TRACK_TESTIMONIALS } from "@/data/track-testimonials";
import { usePageContentOverride } from "@/hooks/usePageContentOverrides";

const VALUE_ICONS = {
    ShieldCheck,
    Hammer,
    Award,
    Clock,
};

const ServiceHero = ({ data, trackCfg, showQuickForm }) => {
    const { phone, phoneHref } = usePhoneNumber();
    return (
        <section
            className="relative w-full overflow-hidden bg-brand-dark text-white min-h-[55vh] flex items-center"
            data-testid="servicedetail-hero"
        >
            <div className="absolute inset-0">
                <img
                    src={data.heroImage}
                    alt={data.heroImageAlt || `${data.name} project by Reformity Inc.`}
                    className="h-full w-full object-cover"
                    loading="eager"
                />
                <div className="absolute inset-0 bg-brand-dark/70" aria-hidden="true" />
                <div
                    className="absolute inset-0 bg-gradient-to-r from-brand-dark/85 via-brand-dark/40 to-transparent"
                    aria-hidden="true"
                />
            </div>
            <Container className="relative z-10 py-16 md:py-20">
                <div
                    className={cn(
                        "grid grid-cols-1 gap-10 items-center",
                        showQuickForm && "lg:grid-cols-12 lg:gap-12",
                    )}
                >
                    <div className={cn(showQuickForm ? "lg:col-span-7" : "max-w-3xl")}>
                        <p
                            className="text-xs uppercase tracking-[0.3em] text-brand-cyan font-semibold mb-4"
                            data-testid="servicedetail-eyebrow"
                        >
                            {data.category}
                        </p>
                        <h1
                            className={cn(
                                "font-display leading-[0.95] tracking-wide text-white",
                                showQuickForm
                                    ? "text-4xl sm:text-5xl lg:text-6xl"
                                    : "text-5xl sm:text-6xl lg:text-7xl xl:text-8xl",
                            )}
                            data-testid="servicedetail-title"
                        >
                            {data.heroTitle}
                        </h1>
                        <p className="mt-6 max-w-2xl text-base md:text-lg text-white/80 font-light italic leading-relaxed">
                            {data.heroSubhead}
                        </p>
                        <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
                            {/* With the form in the hero the quote button would
                                scroll past it, so it becomes the phone-first
                                pair; without the form it stays the primary CTA. */}
                            {!showQuickForm && (
                                <BrandButton asChild variant="primary" size="lg">
                                    <Link
                                        to={`${trackCfg.contact}?service=${data.slug}`}
                                        data-testid="servicedetail-cta-quote"
                                    >
                                        Get a Free Quote
                                        <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
                                    </Link>
                                </BrandButton>
                            )}
                            <BrandButton asChild variant="outline" tone="onDark" size="lg">
                                <a href={phoneHref} data-testid="servicedetail-cta-call">
                                    <Phone className="h-4 w-4" strokeWidth={2.25} />
                                    Call {phone}
                                </a>
                            </BrandButton>
                        </div>
                    </div>
                    {showQuickForm && (
                        <div className="lg:col-span-5">
                            <QuickQuoteForm
                                serviceSlug={data.slug}
                                serviceName={data.name}
                                data-testid="servicedetail-quickquote"
                            />
                        </div>
                    )}
                </div>
            </Container>
        </section>
    );
};

const ValueStrip = ({ data }) => (
    <Section data-testid="servicedetail-value">
        <Container>
            <div className="max-w-2xl mb-10 md:mb-14">
                <p className="text-xs uppercase tracking-[0.25em] text-brand-coral font-semibold mb-3">
                    Why Reformity
                </p>
                <h2 className="font-display text-4xl sm:text-5xl tracking-wide text-brand-dark leading-none">
                    Why {data.name.toLowerCase()} with Reformity.
                </h2>
                <p className="mt-5 text-base text-brand-dark/75 leading-relaxed">{data.intro}</p>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {data.valueBullets.map((b, i) => {
                    const Icon = VALUE_ICONS[b.icon] || ShieldCheck;
                    return (
                        <li
                            key={i}
                            className="bg-white border border-brand-dark/10 p-6 group hover:border-brand-cyan transition-colors duration-200"
                            data-testid={`servicedetail-value-${i}`}
                        >
                            <span className="flex h-11 w-11 items-center justify-center bg-brand-coral/10 text-brand-coral mb-4 group-hover:bg-brand-coral group-hover:text-white transition-colors">
                                <Icon className="h-5 w-5" strokeWidth={2.25} />
                            </span>
                            <h3 className="font-sans font-bold text-base text-brand-dark mb-2">
                                {b.title}
                            </h3>
                            <p className="text-sm text-brand-dark/70 leading-relaxed">
                                {b.body}
                            </p>
                        </li>
                    );
                })}
            </ul>
        </Container>
    </Section>
);

const Materials = ({ data }) => (
    <Section dark data-testid="servicedetail-materials">
        <Container>
            <div className="max-w-2xl mb-10 md:mb-14">
                <p className="text-xs uppercase tracking-[0.25em] text-brand-cyan font-semibold mb-3">
                    Materials &amp; Styles
                </p>
                <h2 className="font-display text-4xl sm:text-5xl tracking-wide text-white leading-none">
                    What we work with.
                </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {data.materials.map((m) => (
                    <article
                        key={m.name}
                        className="group bg-[#333] border border-white/10 overflow-hidden hover:border-brand-cyan transition-colors"
                        data-testid={`servicedetail-material-${m.name.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                        <div className="aspect-[4/3] overflow-hidden bg-brand-dark">
                            <img
                                src={m.image}
                                alt={m.alt || m.name}
                                loading="lazy"
                                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                        </div>
                        <div className="p-5">
                            <h3 className="font-display text-xl tracking-wide text-white mb-2">
                                {m.name}
                            </h3>
                            <p className="text-sm text-white/65 leading-relaxed">{m.desc}</p>
                        </div>
                    </article>
                ))}
            </div>
        </Container>
    </Section>
);

const Process = ({ data }) => (
    <Section data-testid="servicedetail-process">
        <Container>
            <div className="max-w-2xl mb-12 md:mb-14">
                <p className="text-xs uppercase tracking-[0.25em] text-brand-coral font-semibold mb-3">
                    Our Process
                </p>
                <h2 className="font-display text-4xl sm:text-5xl tracking-wide text-brand-dark leading-none">
                    From quote to clean-up.
                </h2>
            </div>
            <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                {data.processSteps.map((p, i) => (
                    <li
                        key={p.num}
                        className="border-t-2 border-brand-coral pt-6"
                        data-testid={`servicedetail-process-${i}`}
                    >
                        <span className="font-display text-5xl md:text-6xl text-brand-coral leading-none block">
                            {p.num}
                        </span>
                        <h3 className="mt-3 font-sans font-bold text-base uppercase tracking-wider text-brand-dark">
                            {p.title}
                        </h3>
                        <p className="mt-2 text-sm text-brand-dark/70 leading-relaxed">
                            {p.body}
                        </p>
                    </li>
                ))}
            </ol>
        </Container>
    </Section>
);

const FeaturedProjects = ({ data, projects }) => (
    <Section dark data-testid="servicedetail-projects">
        <Container>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-12">
                <div className="max-w-xl">
                    <p className="text-xs uppercase tracking-[0.25em] text-brand-cyan font-semibold mb-3">
                        Recent {data.name} Work
                    </p>
                    <h2 className="font-display text-4xl sm:text-5xl tracking-wide text-white leading-none">
                        A few we're proud of.
                    </h2>
                </div>
                <Link
                    to={`/gallery?category=${data.slug}`}
                    className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-white hover:text-brand-cyan transition-colors"
                    data-testid="servicedetail-gallery-link"
                >
                    View Full Gallery
                    <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
                </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {projects.slice(0, 3).map((p) => (
                    <Link
                        key={p.id}
                        to={`/gallery?category=${data.slug}`}
                        className="group relative block overflow-hidden bg-brand-dark aspect-[4/3] border border-white/10 hover:border-brand-cyan transition-colors"
                        data-testid={`servicedetail-project-${p.id}`}
                    >
                        <img
                            src={p.image}
                            alt={`${p.title} in ${p.city}`}
                            loading="lazy"
                            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <div
                            className="absolute inset-0 bg-gradient-to-t from-brand-dark via-brand-dark/30 to-transparent opacity-90"
                            aria-hidden="true"
                        />
                        <div className="absolute inset-x-0 bottom-0 p-5">
                            <h3 className="font-display text-xl sm:text-2xl tracking-wide text-white leading-tight">
                                {p.title}
                            </h3>
                            <p className="text-xs text-white/70 mt-1 flex items-center gap-1.5">
                                <MapPin className="h-3 w-3" strokeWidth={2.5} />
                                {p.city}
                            </p>
                        </div>
                    </Link>
                ))}
            </div>
        </Container>
    </Section>
);

const PLATFORM_ORDER = ["Google", "Homestars", "Bark", "Houzz", "BBB"];

const TestimonialStrip = ({ data, trackCfg }) => {
    const isRes = trackCfg.key === "residential";
    // Residential: real reviews filtered to this service (fall back to top mix).
    // Commercial/industrial: track-specific property-manager/site-supervisor quotes.
    const matching = TESTIMONIALS.filter((t) => t.service === data.slug);
    const featured = isRes
        ? (matching.length >= 3 ? matching : TESTIMONIALS).slice(0, 3)
        : (TRACK_TESTIMONIALS[trackCfg.key] || TESTIMONIALS).slice(0, 3);

    return (
        <Section
            className="py-16 md:py-20"
            bg="white"
            data-testid={`servicedetail-testimonials-${data.slug}`}
        >
            <Container>
                <div className="mb-10 md:mb-12">
                    <p className="text-xs uppercase tracking-[0.25em] text-brand-coral font-semibold mb-3">
                        {trackCfg.audienceEyebrow}
                    </p>
                    <h2 className="font-display text-4xl sm:text-5xl tracking-wide text-brand-dark leading-none">
                        {trackCfg.audienceLine}
                    </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                    {featured.map((t) => (
                        <article
                            key={t.id}
                            className="bg-white border border-brand-dark/10 p-6 flex flex-col h-full hover:border-brand-cyan transition-colors"
                            data-testid={`servicedetail-testimonial-${t.id}`}
                        >
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-1">
                                    {Array.from({ length: 5 }).map((_, i) => (
                                        <Star
                                            key={i}
                                            className="h-4 w-4 text-brand-cyan fill-brand-cyan"
                                            strokeWidth={1.5}
                                        />
                                    ))}
                                </div>
                                {t.verified && (
                                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-brand-cyan bg-brand-cyan/10 px-2 py-1">
                                        <BadgeCheck className="h-3 w-3" strokeWidth={2.5} />
                                        Verified
                                    </span>
                                )}
                            </div>
                            <blockquote className="font-light italic text-sm text-brand-dark/85 leading-relaxed flex-1">
                                {`"${t.quote}"`}
                            </blockquote>
                            <footer className="mt-4 pt-4 border-t border-brand-dark/10">
                                <p className="font-semibold text-sm text-brand-dark">
                                    {t.name}
                                </p>
                                <p className="text-xs text-brand-gray">
                                    {t.role ? t.role : `via ${t.source}`}
                                </p>
                            </footer>
                        </article>
                    ))}
                </div>

                {isRes && (
                    <div
                        className="mt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 pt-8 border-t border-brand-dark/10"
                        data-testid={`servicedetail-testimonials-${data.slug}-footer`}
                    >
                        <Link
                            to="/reviews"
                            className="inline-flex items-center gap-2 text-sm font-semibold text-brand-cyan hover:text-brand-coral transition-colors"
                            data-testid={`servicedetail-testimonials-${data.slug}-see-all`}
                        >
                            See all 157 verified reviews
                            <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
                        </Link>
                        <div
                            className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] uppercase tracking-wider text-brand-gray"
                            data-testid={`servicedetail-testimonials-${data.slug}-platforms`}
                        >
                            <span className="font-semibold text-brand-dark/70">
                                Verified on
                            </span>
                            {PLATFORM_ORDER.map((name, idx) => {
                                const slug = name.toLowerCase();
                                const meta = REVIEW_SOURCES[name];
                                const label = meta?.rating
                                    ? `${name} ${meta.rating.toFixed(1)}★`
                                    : name;
                                return (
                                    <span key={name} className="inline-flex items-center">
                                        {idx > 0 && (
                                            <span className="text-brand-dark/20 mr-2">
                                                ·
                                            </span>
                                        )}
                                        <Link
                                            to={`/reviews/${slug}`}
                                            className="text-brand-dark/80 hover:text-brand-cyan font-semibold transition-colors"
                                            data-testid={`servicedetail-testimonials-${data.slug}-platform-${slug}`}
                                        >
                                            {label}
                                        </Link>
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                )}
            </Container>
        </Section>
    );
};

const FAQ = ({ data }) => (
    <Section data-testid="servicedetail-faq">
        <Container size="narrow">
            <div className="mb-8 md:mb-10">
                <p className="text-xs uppercase tracking-[0.25em] text-brand-coral font-semibold mb-3">
                    Frequently Asked Questions
                </p>
                <h2 className="font-display text-4xl sm:text-5xl tracking-wide text-brand-dark leading-none">
                    Quick answers.
                </h2>
            </div>
            <FAQAccordion items={data.faqs} testidPrefix={`servicedetail-faq-${data.slug}`} />
        </Container>
    </Section>
);

const CtaBand = ({ data, trackCfg }) => {
    const { phone, phoneHref } = usePhoneNumber();
    return (
    <Section dark className="py-16 md:py-20" data-testid="servicedetail-cta-band">
        <Container>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-7">
                    <p className="text-xs uppercase tracking-[0.25em] text-brand-cyan font-semibold mb-3">
                        Ready to Build?
                    </p>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-wide text-white leading-tight">
                        Free {data.name.toLowerCase()} quote in under 24 hours.
                    </h2>
                </div>
                <div className="lg:col-span-5 flex flex-col gap-3 lg:items-end">
                    <BrandButton asChild variant="primary" size="lg" className="w-full lg:w-auto">
                        <Link
                            to={`${trackCfg.contact}?service=${data.slug}`}
                            data-testid="servicedetail-cta-bottom-quote"
                        >
                            Start My Free Quote
                            <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
                        </Link>
                    </BrandButton>
                    <BrandButton
                        asChild
                        variant="outline"
                        tone="onDark"
                        size="lg"
                        className="w-full lg:w-auto"
                    >
                        <a href={phoneHref}>
                            <Phone className="h-4 w-4" strokeWidth={2.25} />
                            Call {phone}
                        </a>
                    </BrandButton>
                </div>
            </div>
        </Container>
    </Section>
    );
};

const ServiceDetail = ({ data, projects = [], track = "residential" }) => {
    const trackCfg = TRACKS[track] || TRACKS.residential;
    const override = usePageContentOverride(`service:${trackCfg.key}:${data.slug}`);
    const servicePath = `${trackCfg.servicesBase}/${data.slug}`;
    const trackLabel = trackCfg.key === "residential" ? "" : `${trackCfg.label} `;

    // Apply admin overrides (DB-first) over the static data defaults.
    const heroImage = override?.hero_image || data.heroImage;
    const heroImageAlt =
        override?.hero_image_alt || `${data.name} project by Reformity Inc.`;
    const materials = (data.materials || []).map((m) => {
        const mo = override?.materials?.find((om) => om.name === m.name);
        return mo?.image ? { ...m, image: mo.image } : m;
    });
    const effectiveData = { ...data, heroImage, heroImageAlt, materials };

    // The in-hero quick form is residential-only: it hard-codes
    // property_type "residential" and posts the page slug as the service, and
    // only the residential slugs are in the backend's residential enum. The
    // commercial/industrial tracks keep the existing link-to-wizard CTA.
    const showQuickForm = trackCfg.key === "residential";

    const staticMeta = PAGE_META[data.slug] || {
        title: data.metaTitle || `${data.name} — ${trackLabel}Contractors GTA | Reformity Inc.`,
        description: data.metaDescription || data.heroSubhead,
    };
    const meta = {
        title: override?.seo_title || staticMeta.title,
        description: override?.seo_description || staticMeta.description,
    };
    const jsonLd = [
        localBusinessSchema(),
        serviceSchema({
            name: data.name,
            description: data.heroSubhead,
            slug: data.slug,
            path: servicePath,
        }),
        faqPageSchema(data.faqs),
        breadcrumbListSchema([
            ...(trackCfg.key === "residential"
                ? []
                : [{ name: trackCfg.label, path: trackCfg.home }]),
            { name: "Services", path: trackCfg.servicesIndex },
            { name: data.name, path: servicePath },
        ]),
    ];
    return (
        <>
            <Seo
                {...meta}
                path={servicePath}
                image={heroImage}
                jsonLd={jsonLd}
            />
            <ServiceHero
                data={effectiveData}
                trackCfg={trackCfg}
                showQuickForm={showQuickForm}
            />
            <ValueStrip data={effectiveData} />
            <Materials data={effectiveData} />
            <Process data={effectiveData} />
            {projects.length > 0 && (
                <FeaturedProjects data={effectiveData} projects={projects} />
            )}
            <FAQ data={effectiveData} />
            <TestimonialStrip data={effectiveData} trackCfg={trackCfg} />
            <CtaBand data={effectiveData} trackCfg={trackCfg} />
        </>
    );
};

export default ServiceDetail;
